import { cn } from '@my/ui';
import { ArrowRight, type LucideIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export interface SectionHeaderProps {
  icon: LucideIcon;
  /** Tailwind text-color class for the icon. */
  iconColor: string;
  title: string;
  /**
   * Override for the trailing link button label. Most callers should pass
   * nothing — the default is the localised `dashboard.viewAll` string. The
   * override exists for the rare case where a panel needs a domain-specific
   * label (e.g. "Open inbox", "See history") that doesn't fit "View all".
   */
  linkLabel?: string;
  /** When provided, renders a trailing chevron link. */
  onLink?: () => void;
}

/**
 * Small section header used at the top of each dashboard panel.
 * "TITLE  →  View all" — uppercase, slate-600, tracking-wider.
 */
export function SectionHeader({
  icon: Icon,
  iconColor,
  title,
  linkLabel,
  onLink,
}: SectionHeaderProps) {
  const { t } = useTranslation('dashboard');
  return (
    <div className="flex items-center justify-between border-b border-slate-200 px-3 py-2">
      <span className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-slate-600">
        <Icon className={cn('h-3.5 w-3.5', iconColor)} />
        {title}
      </span>
      {onLink && (
        <button
          type="button"
          onClick={onLink}
          className="flex items-center gap-0.5 text-[11px] text-blue-500 hover:text-blue-700"
        >
          {linkLabel ?? t('viewAll')}
          <ArrowRight className="h-3 w-3" />
        </button>
      )}
    </div>
  );
}
